import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import { styled } from 'styled-components';
import MaterialIcon from '../leftAppSidebar/MaterialIcon';

const BreadcrumbNav = styled.nav`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #78716c;
    font-family: ${(props) => props.theme.app.font};
`;

const BreadcrumbLink = styled.a`
    color: #78716c;
    text-decoration: none;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    font: inherit;

    &:hover {
        color: #263238;
        text-decoration: underline;
    }
`;

const BreadcrumbSeparator = styled.span`
    display: flex;
    align-items: center;

    .material-symbols-outlined {
        font-size: 16px;
        color: #78716c;
    }
`;

const BreadcrumbCurrent = styled.span`
    color: #78716c;
    font-weight: 600;
`;

// Keep the old export name for backward compatibility with styled-component references
export const BackButtonContainer = BreadcrumbNav;

const getPageNameFromUrl = (): string | null => {
    const pathname = window.location.pathname;
    // Find /Admin/ in the path and take everything after it
    const adminIndex = pathname.toLowerCase().indexOf('/admin/');
    if (adminIndex === -1) return null;
    const afterAdmin = pathname.substring(adminIndex + '/admin/'.length);
    // Get first meaningful segment
    const segments = afterAdmin.split('/').filter(Boolean);
    if (segments.length === 0) return null;

    const segment = segments[0];
    // Convert kebab-case or camelCase to Title Case
    return segment
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface BackButtonProps {
    onClick?: () => void;
    className?: string;
    children?: React.ReactNode;
    isStep?: boolean;
}

export const BackButton: FC<BackButtonProps> = ({ onClick, className, children }) => {
    const handleClick = () => {
        // Emit custom event to allow other components (e.g. unsaved-changes guard) to intercept navigation
        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: { isBack: true },
            cancelable: true
        });
        const allowed = window.dispatchEvent(navigationEvent);
        if (!allowed) return;

        if (onClick) {
            onClick();
            return;
        }
        window.history.back();
    };

    const pageName = getPageNameFromUrl();

    return (
        <BreadcrumbNav className={className} aria-label='Breadcrumb'>
            <BreadcrumbLink href='#' onClick={handleClick}>
                {T._('Home', 'Admin')}
            </BreadcrumbLink>
            {pageName && (
                <>
                    <BreadcrumbSeparator aria-hidden='true'>
                        <MaterialIcon name='chevron_right' />
                    </BreadcrumbSeparator>
                    <BreadcrumbCurrent>{pageName}</BreadcrumbCurrent>
                </>
            )}
        </BreadcrumbNav>
    );
};
